import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { CreateProjectRequest, Project } from '../../models/project.model';
import { NotificationService } from '../../services/notification.service';
import { ProjectService } from '../../services/project.service';
import { ProjectEnvironmentService } from '../../services/project-environment.service';
import { ProjectTestStageService } from '../../services/project-test-stage.service';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { PersonOption, PersonSelectComponent } from '../person-select/person-select.component';
import { LadeZustand, VerwaltungStore } from '../verwaltung/verwaltung.store';
import { kuerzelVorschlag } from './kuerzel-vorschlag';
import { TeamSchrittComponent, TeamZeile } from './team-schritt/team-schritt.component';
import {
  Schritt2Daten,
  UmgebungenTeststufenSchrittComponent,
  doppelteUmgebungen,
  schritt2AusVorlage,
  schritt2Vorbelegung,
} from './umgebungen-teststufen-schritt/umgebungen-teststufen-schritt.component';

export interface ProjektAngelegt {
  id: string;
  name: string;
  teamEingeladen: boolean;
}

type Schritt = 1 | 2 | 3;
type LeitungZustand = 'laedt' | 'bereit' | 'personenFehler' | 'profilFehler';

export const LEERES_PROJEKT = 'leer';
const KUERZEL_FORMAT = /^[A-Z0-9]{2,6}$/;

const SCHRITTE: readonly { nr: Schritt; schluessel: string; optional: boolean }[] = [
  { nr: 1, schluessel: 'projektNeu.schritt.name', optional: false },
  { nr: 2, schluessel: 'projektNeu.schritt.umgebungen', optional: true },
  { nr: 3, schluessel: 'projektNeu.schritt.team', optional: true },
];

function pflicht(control: AbstractControl<string>): ValidationErrors | null {
  return control.value.trim() ? null : { pflicht: true };
}

@Component({
  selector: 'app-projekt-neu-assistent',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    PersonSelectComponent,
    ReactiveFormsModule,
    TeamSchrittComponent,
    TranslatePipe,
    UmgebungenTeststufenSchrittComponent,
  ],
  templateUrl: './projekt-neu-assistent.component.html',
  styleUrl: './projekt-neu-assistent.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjektNeuAssistentComponent {
  private readonly store = inject(VerwaltungStore);
  private readonly translate = inject(TranslateService);
  private readonly umgebungenApi = inject(ProjectEnvironmentService);
  private readonly teststufenApi = inject(ProjectTestStageService);
  private readonly notification = inject(NotificationService);
  private readonly projektApi = inject(ProjectService);

  readonly projekte = input<Project[]>([]);
  readonly projekteZustand = input<LadeZustand>('bereit');
  readonly abbrechen = output<void>();
  readonly angelegt = output<ProjektAngelegt>();

  readonly fieldLengths = FIELD_LENGTHS;
  readonly kuerzelMax = 6;
  readonly leer = LEERES_PROJEKT;
  readonly schritte = SCHRITTE;

  readonly schritt = signal<Schritt>(1);
  readonly fertig = signal<ReadonlySet<Schritt>>(new Set());
  private readonly erreicht = signal<ReadonlySet<Schritt>>(new Set([1]));
  readonly schritt2 = signal<Schritt2Daten | null>(null);
  readonly vorlageLaedt = signal(false);
  readonly kuerzelVonHand = signal(false);
  readonly team = signal<TeamZeile[]>([]);
  readonly ichId = this.store.angemeldeteId;
  readonly speichert = signal(false);
  private readonly vergebeneNamen = new Set<string>();
  private readonly vergebeneKuerzel = new Set<string>();

  readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [pflicht, Validators.maxLength(FIELD_LENGTHS.NAME_MAX), (c) => this.nameVergeben(c)],
    }),
    kuerzel: new FormControl('', {
      nonNullable: true,
      validators: [pflicht, Validators.pattern(KUERZEL_FORMAT), (c) => this.kuerzelVergeben(c)],
    }),
    leadUserId: new FormControl<string | null>(null, Validators.required),
    vorlage: new FormControl(LEERES_PROJEKT, { nonNullable: true }),
    beschreibung: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(FIELD_LENGTHS.FREETEXT_MAX)],
    }),
  });

  private readonly nameFeld = viewChild<ElementRef<HTMLInputElement>>('nameFeld');
  private readonly formEreignis = toSignal(this.form.events, { initialValue: null });

  readonly werte = computed(() => {
    this.formEreignis();
    return this.form.getRawValue();
  });

  readonly vorschlag = computed(() => kuerzelVorschlag(this.werte().name));

  readonly vorschlagAnbieten = computed(
    () => this.kuerzelVonHand() && this.vorschlag() !== '' && this.vorschlag() !== this.werte().kuerzel,
  );

  readonly leitungZustand = computed<LeitungZustand>(() => {
    const zustand = this.store.personenZustand();
    if (zustand === 'fehler') return this.store.angemeldeteId() ? 'personenFehler' : 'profilFehler';
    return zustand;
  });

  readonly leitungsOptionen = computed<PersonOption[]>(() => {
    const ich = this.store.angemeldeteId();
    if (this.leitungZustand() === 'personenFehler') {
      return [{ id: ich!, displayName: this.translate.instant('projektNeu.ich') }];
    }
    return (this.store.personen() ?? [])
      .filter((p) => p.status === 'ACTIVE')
      .map((p) => ({
        id: p.id,
        displayName:
          p.id === ich ? this.translate.instant('projektNeu.leitung.ichName', { name: p.displayName }) : p.displayName,
      }));
  });

  readonly leitungHinweis = computed(() => {
    if (this.leitungZustand() === 'personenFehler') return this.translate.instant('projektNeu.leitung.ladefehler');
    const lead = this.werte().leadUserId;
    if (!lead || lead === this.store.angemeldeteId()) return this.translate.instant('projektNeu.leitung.selbst');
    const name = this.store.personen()?.find((p) => p.id === lead)?.displayName ?? '';
    return this.translate.instant('projektNeu.leitung.eingeladen', { name });
  });

  readonly aktivePersonen = computed(() =>
    this.leitungZustand() === 'bereit' ? (this.store.personen() ?? []).filter((p) => p.status === 'ACTIVE') : [],
  );

  readonly ichName = computed(() => {
    const ich = this.store.angemeldeteId();
    return this.store.personen()?.find((p) => p.id === ich)?.displayName ?? this.translate.instant('projektNeu.ich');
  });

  readonly vorlagen = computed(() =>
    this.projekteZustand() === 'fehler'
      ? []
      : [...this.projekte()].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
  );

  readonly schritt1Gueltig = computed(() => {
    this.formEreignis();
    const { name, kuerzel, leadUserId } = this.form.controls;
    const leitung = this.leitungZustand();
    return (
      name.valid &&
      kuerzel.valid &&
      leadUserId.valid &&
      (leitung === 'bereit' || leitung === 'personenFehler') &&
      this.projekteZustand() !== 'laedt'
    );
  });

  readonly schritt2Gueltig = computed(
    () => !this.vorlageLaedt() && doppelteUmgebungen(this.schritt2()?.umgebungen ?? []).size === 0,
  );

  constructor() {
    this.form.controls.vorlage.valueChanges
      .pipe(
        switchMap((id) => {
          if (id === LEERES_PROJEKT) {
            this.schritt2.set(schritt2Vorbelegung(this.translate));
            this.vorlageLaedt.set(false);
            return EMPTY;
          }
          this.vorlageLaedt.set(true);
          return forkJoin([this.umgebungenApi.list(id), this.teststufenApi.list(id)]).pipe(
            map(([umgebungen, stufen]) => schritt2AusVorlage(umgebungen, stufen, this.translate)),
            catchError(() => {
              this.notification.error('projektNeu.vorlage.ladefehler');
              return of(schritt2Vorbelegung(this.translate));
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((daten) => {
        this.schritt2.set(daten);
        this.vorlageLaedt.set(false);
      });

    this.form.controls.leadUserId.valueChanges.pipe(takeUntilDestroyed()).subscribe((lead) => {
      this.team.update((t) => t.filter((z) => z.personId !== lead));
    });

    this.form.controls.name.valueChanges.pipe(takeUntilDestroyed()).subscribe((name) => {
      if (!this.kuerzelVonHand()) this.form.controls.kuerzel.setValue(kuerzelVorschlag(name));
    });

    effect(() => {
      this.projekte();
      untracked(() => {
        this.form.controls.name.updateValueAndValidity();
        this.form.controls.kuerzel.updateValueAndValidity();
      });
    });

    effect(() => {
      const ich = this.store.angemeldeteId();
      untracked(() => {
        if (ich && !this.form.controls.leadUserId.value) this.form.controls.leadUserId.setValue(ich);
      });
    });

    afterNextRender(() => this.nameFeld()?.nativeElement.focus());
  }

  erreichbar(nr: Schritt): boolean {
    if (this.speichert()) return false;
    if (nr === 1) return true;
    if (!this.schritt1Gueltig() || (nr === 3 && !this.schritt2Gueltig())) return false;
    return this.erreicht().has(nr) || this.fertig().has((nr - 1) as Schritt);
  }

  erledigt(nr: Schritt): boolean {
    return this.fertig().has(nr) && this.schritt() !== nr;
  }

  schrittWaehlen(nr: Schritt): void {
    if (this.erreichbar(nr)) this.zuSchritt(nr);
  }

  weiter(): void {
    if (!this.schritt1Gueltig()) return;
    const aktuell = this.schritt();
    if (aktuell === 2 && !this.schritt2Gueltig()) return;
    this.fertig.update((f) => new Set(f).add(aktuell));
    if (aktuell < 3) this.zuSchritt((aktuell + 1) as Schritt);
  }

  zurueck(): void {
    const aktuell = this.schritt();
    if (aktuell > 1) this.schritt.set((aktuell - 1) as Schritt);
  }

  ueberspringen(): void {
    if (this.schritt2Gueltig()) this.zuSchritt(3);
  }

  private zuSchritt(nr: Schritt): void {
    // Die Vorbelegung entsteht erst hier, damit ihre Namen in der geladenen Sprache stehen.
    if (nr === 2 && !this.schritt2()) this.schritt2.set(schritt2Vorbelegung(this.translate));
    this.erreicht.update((e) => new Set(e).add(nr));
    this.schritt.set(nr);
  }

  kuerzelEingabe(event: Event): void {
    const wert = (event.target as HTMLInputElement).value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    this.kuerzelVonHand.set(wert !== '');
    this.form.controls.kuerzel.setValue(wert === '' ? this.vorschlag() : wert);
  }

  anlegen(): void {
    if (this.speichert() || !this.schritt1Gueltig()) return;
    this.speichert.set(true);
    const teamEingeladen = this.team().length > 0 || this.form.controls.leadUserId.value !== this.store.angemeldeteId();
    this.projektApi.create(this.anfrage()).subscribe({
      next: (projekt) => {
        this.speichert.set(false);
        this.angelegt.emit({ id: projekt.id, name: projekt.name, teamEingeladen });
      },
      error: (fehler: unknown) => {
        this.speichert.set(false);
        this.anlegenFehlgeschlagen(fehler);
      },
    });
  }

  private anfrage(): CreateProjectRequest {
    const { name, kuerzel, leadUserId, vorlage, beschreibung } = this.form.getRawValue();
    const { umgebungen, stufen } = this.schritt2() ?? schritt2Vorbelegung(this.translate);
    const umgebungsName = new Map(umgebungen.map((u) => [u.schluessel, u.name.trim()]));
    return {
      name: name.trim(),
      kuerzel,
      leadUserId: leadUserId!,
      ...(beschreibung.trim() ? { description: beschreibung.trim() } : {}),
      ...(vorlage !== LEERES_PROJEKT ? { templateProjectId: vorlage } : {}),
      environments: umgebungen
        .filter((u) => u.name.trim())
        .map((u) => ({ name: u.name.trim(), url: u.url.trim() || null })),
      testStages: stufen
        .filter((s) => s.an && s.name.trim())
        .map((s) => ({ name: s.name.trim(), environmentName: (s.umgebung && umgebungsName.get(s.umgebung)) || null })),
      members: this.team().map((z) => ({ tenantUserId: z.personId, role: z.rolle })),
    };
  }

  private anlegenFehlgeschlagen(fehler: unknown): void {
    const antwort = fehler instanceof HttpErrorResponse ? fehler : null;
    const code = antwort?.status === 409 ? antwort.error?.code : null;
    if (code === 'PROJECT_NAME_TAKEN') {
      this.vergebeneNamen.add(this.form.controls.name.value.trim().toLowerCase());
      this.zurueckMitFehler(this.form.controls.name);
    } else if (code === 'PROJECT_KUERZEL_TAKEN') {
      this.vergebeneKuerzel.add(this.form.controls.kuerzel.value);
      this.zurueckMitFehler(this.form.controls.kuerzel);
    } else {
      this.notification.error(
        antwort?.status === 400 ? 'projektNeu.anlegen.fehlerEingabe' : 'projektNeu.anlegen.fehler',
      );
    }
  }

  private zurueckMitFehler(feld: AbstractControl): void {
    feld.updateValueAndValidity();
    feld.markAsTouched();
    this.schritt.set(1);
  }

  erneutLaden(): void {
    this.store.personenAktualisieren();
  }

  vorschlagUebernehmen(): void {
    this.kuerzelVonHand.set(false);
    this.form.controls.kuerzel.setValue(this.vorschlag());
  }

  nameFehler(): string | null {
    const fehler = this.form.controls.name.errors;
    if (!fehler) return null;
    if (fehler['pflicht']) return 'projektNeu.name.pflicht';
    if (fehler['vergeben']) return 'projektNeu.name.vergeben';
    return 'projektNeu.name.zuLang';
  }

  kuerzelFehler(): string | null {
    const fehler = this.form.controls.kuerzel.errors;
    if (!fehler) return null;
    if (fehler['pflicht']) return 'projektNeu.kuerzel.pflicht';
    if (fehler['pattern']) return 'projektNeu.kuerzel.format';
    return 'projektNeu.kuerzel.vergeben';
  }

  private nameVergeben(control: AbstractControl<string>): ValidationErrors | null {
    const name = control.value.trim().toLowerCase();
    const vergeben = this.vergebeneNamen.has(name) || this.projekte().some((p) => p.name.trim().toLowerCase() === name);
    return name && vergeben ? { vergeben: true } : null;
  }

  private kuerzelVergeben(control: AbstractControl<string>): ValidationErrors | null {
    const vergeben =
      this.vergebeneKuerzel.has(control.value) || this.projekte().some((p) => p.kuerzel === control.value);
    return control.value && vergeben ? { vergeben: true } : null;
  }
}
